/**
 * CardService.ts
 * 
 * 职责：
 * 1. 管理画布上的所有卡片元素的数据和状态
 * 2. 提供卡片操作的核心方法，如添加、删除和修改卡片
 * 3. 处理卡片绘制和渲染的逻辑
 * 4. 管理卡片属性，如背景颜色、边框颜色和圆角
 * 5. 提供卡片元素的碰撞检测功能
 * 6. 负责将卡片元素渲染到画布上
 * 7. 提供卡片元素的增删改查功能
 */

import { Point, CardData } from '../../types';
import { ViewportService } from './ViewportService';
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import AutoExpandTextarea from '../common/AutoExpandTextarea';

// 定义卡片大小级别
interface CardSizeLevel {
  name: string;   // 级别名称
  width: number;  // 卡片宽度
  height: number; // 卡片高度
  maxChars: number; // 该级别最大字符数
}

export class CardService {
  private ctx: CanvasRenderingContext2D | null = null;
  private cards: CardData[] = [];
  private backgroundColor: string = '#FFFFFF'; // 白色背景
  private borderColor: string = '#000000'; // 黑色边框
  private borderRadius: number = 4; // 默认圆角
  private defaultWidth: number = 120; // 减小默认卡片宽度
  private defaultHeight: number = 75; // 按照黄金比例(1:1.618)计算的高度约为100
  private viewportService: ViewportService | null = null;
  private mouseMoveHandler: ((e: MouseEvent) => void) | null = null; // 存储鼠标移动处理函数
  private mouseMoveTimer: number | null = null; // 存储鼠标移动计时器
  
  // 卡片大小增量参数
  private widthIncrement: number = 40; // 每次扩展时宽度增加40px
  private heightIncrement: number = 25; // 每次扩展时高度增加25px
  
  // 保留作为参考的卡片大小级别
  private cardSizeLevelsReference: CardSizeLevel[] = [
    { name: '小', width: 120, height: 75, maxChars: 50 },
    { name: '中', width: 160, height: 100, maxChars: 100 },
    { name: '大', width: 240, height: 150, maxChars: 200 },
    { name: '特大', width: 320, height: 200, maxChars: 350 }
  ];

  // 卡片选择和操作状态
  private isDragging: boolean = false;
  private isResizing: boolean = false;
  private lastMousePoint: Point = { x: 0, y: 0 };
  
  // 用于触发画布重绘的回调函数
  private onRenderCallback: (() => void) | null = null;

  constructor() {
    // 添加事件监听器，在滚动动画的每一帧更新编辑元素位置
    document.addEventListener('viewport-scroll-update', () => {
      // 检查是否有正在编辑的卡片
      if (this.cards.some(card => card.isEditing)) {
        this.updateEditingElementsPosition();
      }
    });
  }

  // 设置画布上下文
  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  // 设置视口服务
  public setViewportService(viewportService: ViewportService): void {
    this.viewportService = viewportService;
  }

  // 获取所有卡片
  public getCards(): CardData[] {
    return this.cards;
  }

  // 设置卡片数据
  public setCards(cards: CardData[]): void {
    this.cards = cards;
  }

  // 设置卡片背景颜色
  public setBackgroundColor(color: string): void {
    this.backgroundColor = color;
  }

  // 获取卡片背景颜色
  public getBackgroundColor(): string {
    return this.backgroundColor;
  }

  // 设置卡片边框颜色
  public setBorderColor(color: string): void {
    this.borderColor = color;
  }

  // 获取卡片边框颜色
  public getBorderColor(): string {
    return this.borderColor;
  }

  // 设置卡片圆角
  public setBorderRadius(radius: number): void {
    this.borderRadius = radius;
  }

  // 获取卡片圆角
  public getBorderRadius(): number {
    return this.borderRadius;
  }

  // 生成唯一ID
  private generateId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }
  
  // 根据内容长度计算适合的卡片尺寸方法已不再需要，直接基于滚动条出现来扩展卡片

  // 添加卡片
  public addCard(x: number, y: number): CardData {
    if (!this.ctx) throw new Error("画布上下文未初始化");
    
    // 检查是否已经有卡片正在编辑
    const isAnyCardEditing = this.cards.some(card => card.isEditing);
    if (isAnyCardEditing) {
      // 如果已有卡片正在编辑，不创建新的卡片
      return {
        id: "",
        x,
        y,
        width: this.defaultWidth,
        height: this.defaultHeight,
        content: "",
        color: this.backgroundColor,
        borderColor: this.borderColor,
        borderRadius: this.borderRadius,
        isEditing: false
      };
    }
    
    // 清理编辑状态的空白卡片
    this.cards = this.cards.filter(card => !card.isEditing || card.content.trim());
    
    // 获取当前视口缩放比例
    const scale = this.viewportService ? this.viewportService.getScale() : 1.0;
    
    // 使用默认尺寸创建新卡片
    const cardData: CardData = {
      id: this.generateId(),
      x,
      y,
      width: this.defaultWidth,
      height: this.defaultHeight,
      content: "",
      color: this.backgroundColor,
      borderColor: this.borderColor,
      borderRadius: this.borderRadius,
      isEditing: true,
      creationScale: scale
      // 不再使用固定的sizeLevel
    };
    
    this.cards.push(cardData);
    this.createCardEditor(cardData);
    
    return cardData;
  }

  // 编码处理 - 统一不同来源的换行符
  private normalizeLineBreaks(text: string): string {
    // 将CRLF(\r\n)替换为LF(\n) - 统一换行符格式
    return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }

  // 创建卡片编辑器
  private createCardEditor(cardData: CardData): void {
    if (!this.ctx || !this.viewportService) return;
    
    // 不再强制禁用画布缩放，保留用户设置的缩放状态
    // this.viewportService.setZoomDisabled(true);
    
    // 获取卡片中心点
    const centerX = cardData.x + cardData.width / 2;
    const centerY = cardData.y + cardData.height / 2;
    
    // 使用viewportService的canvasToClient方法将画布坐标转换为客户端坐标
    const clientPos = this.viewportService.canvasToClient(centerX, centerY);
    const scale = this.viewportService.getScale();
    
    // 添加鼠标处理样式
    const styleSheet = document.createElement('style');
    styleSheet.id = 'card-editor-style';
    styleSheet.textContent = `
      [data-card-editor-container="true"] {
        cursor: text;
      }
      [data-card-editor-container="true"]:hover {
        cursor: text;
      }
      body.card-editing {
        cursor: none !important;
      }
      body.card-editing * {
        cursor: none !important;
      }
      body.card-editing.cursor-moving {
        cursor: text !important;
      }
      body.card-editing.cursor-moving * {
        cursor: text !important;
      }
    `;
    
    if (!document.getElementById('card-editor-style')) {
      document.head.appendChild(styleSheet);
    }
    
    // 创建容器元素
    const container = document.createElement('div');
    container.setAttribute('data-card-editor-container', 'true');
    container.setAttribute('data-card-id', cardData.id);
    container.style.position = 'absolute';
    
    // 计算缩放后的尺寸
    const scaledWidth = cardData.width * scale;
    const scaledHeight = cardData.height * scale;
    
    // 设置容器位置（使用transform确保精确定位）
    const translateX = Math.round(clientPos.x - scaledWidth / 2);
    const translateY = Math.round(clientPos.y - scaledHeight / 2);
    container.style.transform = `translate(${translateX}px, ${translateY}px)`;
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = `${scaledWidth}px`;
    container.style.height = `${scaledHeight}px`;
    container.style.zIndex = '9999'; // 提高 z-index 确保在最上层
    container.style.backgroundColor = cardData.color;
    container.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
    container.style.border = `${2 * scale}px solid ${cardData.borderColor}`;
    container.style.borderRadius = `${cardData.borderRadius * scale}px`;
    container.style.boxSizing = 'border-box';
    container.style.padding = `${8 * scale}px`;
    container.style.transition = 'none'; // 移除过渡动画，以便实时更新位置和大小
    container.style.transformOrigin = 'center'; // 设置变换原点为中心
    
    // 添加视口变化事件监听器，实时更新卡片
    const viewportChangedHandler = () => {
      this.updateEditingElementsPosition();
    };
    document.addEventListener('viewport-changed', viewportChangedHandler);
    
    // 添加视口滚动更新事件监听器，平滑动画期间实时更新
    const viewportScrollUpdateHandler = () => {
      this.updateEditingElementsPosition();
    };
    document.addEventListener('viewport-scroll-update', viewportScrollUpdateHandler);
    
    // 保存事件处理函数到容器元素，以便稍后删除
    (container as any)._viewportChangedHandler = viewportChangedHandler;
    (container as any)._viewportScrollUpdateHandler = viewportScrollUpdateHandler;
    
    // 将容器添加到文档中
    document.body.appendChild(container);
    
    // 添加容器点击事件处理
    const containerClickHandler = (e: MouseEvent) => {
      e.stopPropagation(); // 阻止事件冒泡
    };
    container.addEventListener('mousedown', containerClickHandler);
    
    // 添加文档点击事件处理
    const documentClickHandler = (e: MouseEvent) => {
      if (!container.contains(e.target as Node)) {
        textarea.blur();
      }
    };
    document.addEventListener('mousedown', documentClickHandler);
    
    // 创建内部包装容器，使用绝对定位方式而不是flex布局
    const wrapperDiv = document.createElement('div');
    wrapperDiv.style.position = 'absolute'; // 使用绝对定位
    wrapperDiv.style.left = '0';
    wrapperDiv.style.top = '0';
    wrapperDiv.style.right = '0';
    wrapperDiv.style.bottom = '0';
    wrapperDiv.style.display = 'flex'; 
    wrapperDiv.style.flexDirection = 'column';
    wrapperDiv.style.justifyContent = 'center';
    wrapperDiv.style.alignItems = 'center';
    
    // 创建textarea元素
    const textarea = document.createElement('textarea');
    textarea.value = cardData.content;
    textarea.setAttribute('data-card-editor', 'true');
    
    // 计算精确的文本域宽度
    const borderWidth = 2 * scale;
    const padding = 8 * scale;
    const contentWidth = Math.max(10, scaledWidth - padding * 2 - borderWidth * 2);
    textarea.style.width = `${contentWidth}px`;
    
    textarea.style.height = 'auto';
    textarea.style.minHeight = '20px';
    textarea.style.boxSizing = 'border-box';
    textarea.style.backgroundColor = 'transparent';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.margin = '0';
    textarea.style.padding = '0';
    textarea.style.overflow = 'hidden';
    textarea.style.fontFamily = 'Arial, sans-serif';
    
    // 使用与画布缩放一致的字体大小，确保最小可读性
    const baseFontSize = 16;
    const fontSize = Math.max(9, baseFontSize * scale);
    textarea.style.fontSize = `${fontSize}px`;
    
    textarea.style.lineHeight = '1.4'; // 使用固定行高
    textarea.style.textAlign = 'center';
    textarea.style.display = 'block';
    textarea.style.whiteSpace = 'pre-wrap'; // 确保保留换行符和空格
    textarea.style.wordWrap = 'break-word';
    textarea.style.color = '#000';
    textarea.style.alignSelf = 'center'; // 确保垂直居中
    // 默认设置多行
    textarea.rows = 1;
    textarea.cols = 1;

    // 添加textarea到包装容器
    wrapperDiv.appendChild(textarea);
    
    // 将包装容器添加到主容器
    container.appendChild(wrapperDiv);
    
    // 保存事件处理器引用以便后续清理
    (container as any)._containerClickHandler = containerClickHandler;
    (container as any)._documentClickHandler = documentClickHandler;
    
    // 添加事件监听器
    textarea.addEventListener('input', (e) => {
      // 保存原始高度以检测溢出
      const originalScrollHeight = textarea.scrollHeight;
      
      // 自动调整高度
      textarea.style.height = '0';
      textarea.style.height = `${Math.min(originalScrollHeight, container.clientHeight * 0.9)}px`;
      
      // 更新卡片内容
      const index = this.cards.findIndex(card => card.id === cardData.id);
      if (index !== -1) {
        // 确保保留所有换行符
        const content = this.normalizeLineBreaks(textarea.value);
        this.cards[index].content = content;
        
        // 检查是否需要调整卡片大小（基于内容是否溢出容器）
        const hasOverflow = originalScrollHeight > container.clientHeight * 0.9;
        this.adjustCardSizeByOverflow(index, container, textarea, scale, hasOverflow);
      }
      
      // 处理滚动条显示
      if (originalScrollHeight > container.clientHeight * 0.9) {
        textarea.style.overflow = 'auto';
      } else {
        textarea.style.overflow = 'hidden';
      }
    });
    
    // 失焦事件处理
    textarea.addEventListener('blur', () => {
      this.finishEditing(cardData.id);
    });
    
    // 按键事件处理
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Esc键取消创建卡片
        e.preventDefault();
        // 找到当前卡片
        const index = this.cards.findIndex(card => card.id === cardData.id);
        if (index !== -1) {
          // 直接删除这个卡片
          this.cards.splice(index, 1);
        }
        // 移除编辑器
        this.removeCardEditor(cardData.id);
        // 触发重新渲染
        if (this.ctx && this.ctx.canvas) {
          const event = new CustomEvent('render-required');
          this.ctx.canvas.dispatchEvent(event);
        }
        // 发送工具操作完成事件
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
        // 清理状态
        document.body.classList.remove('card-editing', 'cursor-moving');
        if (this.mouseMoveHandler) {
          document.removeEventListener('mousemove', this.mouseMoveHandler);
          this.mouseMoveHandler = null;
        }
        if (this.mouseMoveTimer !== null) {
          window.clearTimeout(this.mouseMoveTimer);
          this.mouseMoveTimer = null;
        }
      } else if (e.key === 'Enter') {
        if (e.ctrlKey || !e.shiftKey) {
          // Ctrl+回车 或 普通回车：完成编辑
          e.preventDefault(); // 阻止默认的换行行为
          textarea.blur();
        }
        // 如果是Shift+回车，不做处理，允许默认的换行行为
      }
    });
    
    // 激活并隐藏鼠标
    document.body.classList.add('card-editing');
    
    // 鼠标移动处理
    this.mouseMoveTimer = null;
    
    this.mouseMoveHandler = () => {
      document.body.classList.add('cursor-moving');
      
      if (this.mouseMoveTimer !== null) {
        window.clearTimeout(this.mouseMoveTimer);
      }
      
      this.mouseMoveTimer = window.setTimeout(() => {
        document.body.classList.remove('cursor-moving');
        this.mouseMoveTimer = null;
      }, 500);
    };
    
    document.addEventListener('mousemove', this.mouseMoveHandler);
    
    // 自动聚焦并触发input事件进行初始调整
    setTimeout(() => {
      textarea.focus();
      const event = new Event('input', { bubbles: true });
      textarea.dispatchEvent(event);
    }, 0);
  }

  // 完成卡片编辑
  private finishEditing(cardId: string): void {
    // 查找编辑中的卡片
    const index = this.cards.findIndex(card => card.id === cardId);
    if (index === -1) return;
    
    const card = this.cards[index];
    
    // 不再强制启用画布缩放，保留用户设置的缩放状态
    // if (this.viewportService) {
    //   this.viewportService.setZoomDisabled(false);
    // }
    
    // 移除鼠标状态和事件监听
    document.body.classList.remove('card-editing', 'cursor-moving');
    
    if (this.mouseMoveHandler) {
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }
    
    if (this.mouseMoveTimer !== null) {
      window.clearTimeout(this.mouseMoveTimer);
      this.mouseMoveTimer = null;
    }
    
    // 获取编辑器元素
    const container = document.querySelector(`div[data-card-editor-container="true"][data-card-id="${cardId}"]`) as HTMLElement;
    if (container) {
      // 获取textarea元素，确保最后一次内容更新
      const textarea = container.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea && textarea.value !== card.content) {
        // 将最终内容保存回卡片，确保所有换行符都被保留
        card.content = this.normalizeLineBreaks(textarea.value);
      }
    }
    
    // 如果卡片内容为空，移除该卡片
    if (!card.content.trim()) {
      this.cards.splice(index, 1);
    } else {
      // 设置卡片为非编辑状态（不再需要在这里调整大小，因为已经在编辑过程中调整了）
      this.cards[index].isEditing = false;
    }
    
    // 移除编辑器
    this.removeCardEditor(cardId);
    
    // 触发重新渲染
    if (this.ctx && this.ctx.canvas) {
      const event = new CustomEvent('render-required');
      this.ctx.canvas.dispatchEvent(event);
    }
    
    // 发送工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }
  
  // 调试卡片内容方法（仅在开发环境中使用）
  private debugCardContent(content: string): void {
    // 移除调试日志
  }
  
  // 基于文本溢出情况调整卡片大小
  private adjustCardSizeByOverflow(
    cardIndex: number, 
    container: HTMLElement, 
    textarea: HTMLTextAreaElement, 
    scale: number,
    hasOverflow: boolean
  ): void {
    const card = this.cards[cardIndex];
    
    // 如果有溢出（即将显示滚动条），则扩展卡片
    if (hasOverflow) {
      // 计算新的尺寸（直接增加一个增量）
      const newWidth = card.width + this.widthIncrement;
      const newHeight = card.height + this.heightIncrement;
      
      // 调整卡片尺寸
      this.resizeCardToSpecificSize(cardIndex, newWidth, newHeight, container, scale);
    }
  }
  
  // 调整卡片到指定尺寸
  private resizeCardToSpecificSize(cardIndex: number, newWidth: number, newHeight: number, container: HTMLElement, scale: number): void {
    const card = this.cards[cardIndex];
    
    // 如果尺寸没变，不做任何事
    if (newWidth === card.width && newHeight === card.height) return;
    
    // 保持卡片中心点不变
    const centerX = card.x + card.width / 2;
    const centerY = card.y + card.height / 2;
    
    // 更新卡片尺寸
    card.width = newWidth;
    card.height = newHeight;
    
    // 更新位置，保持中心点不变
    card.x = centerX - card.width / 2;
    card.y = centerY - card.height / 2;
    
    // 计算缩放后的卡片尺寸
    const scaledWidth = card.width * scale;
    const scaledHeight = card.height * scale;
    
    // 获取边框宽度和内边距
    const borderWidth = 2 * scale;
    const padding = 8 * scale;
    
    // 获取中心点的屏幕坐标
    const clientPos = this.viewportService!.canvasToClient(centerX, centerY);
    
    // 更新编辑器容器尺寸 - 使用transform代替left/top
    const translateX = clientPos.x - scaledWidth / 2;
    const translateY = clientPos.y - scaledHeight / 2;
    container.style.transform = `translate(${translateX}px, ${translateY}px)`;
    container.style.left = '0';
    container.style.top = '0';
    
    // 更新容器大小和样式，使用精确值
    container.style.width = `${scaledWidth}px`;
    container.style.height = `${scaledHeight}px`;
    container.style.border = `${borderWidth}px solid ${card.borderColor}`;
    container.style.borderRadius = `${card.borderRadius * scale}px`;
    container.style.padding = `${padding}px`;
    
    // 更新内部包装容器，确保垂直居中
    const wrapperDiv = container.querySelector('div') as HTMLElement;
    if (wrapperDiv) {
      wrapperDiv.style.position = 'absolute';
      wrapperDiv.style.left = '0';
      wrapperDiv.style.top = '0';
      wrapperDiv.style.right = '0';
      wrapperDiv.style.bottom = '0';
      wrapperDiv.style.display = 'flex';
      wrapperDiv.style.flexDirection = 'column';
      wrapperDiv.style.justifyContent = 'center';
      wrapperDiv.style.alignItems = 'center';
    }
    
    // 更新文本编辑器
    const textarea = container.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      // 使用精确宽度
      textarea.style.width = `${scaledWidth - padding * 2 - borderWidth * 2}px`;
      textarea.style.fontSize = `${16 * scale}px`;
      textarea.style.lineHeight = '1.4';
      textarea.style.textAlign = 'center';
      textarea.style.alignSelf = 'center';
      textarea.style.margin = '0';
      textarea.style.padding = '0';
      
      // 异步调整高度
      setTimeout(() => {
        const originalScrollHeight = textarea.scrollHeight;
        textarea.style.height = '0';
        const maxHeight = container.clientHeight * 0.9 - borderWidth * 2 - padding * 2;
        textarea.style.height = `${Math.min(originalScrollHeight, maxHeight)}px`;
      }, 0);
    }
  }

  // 更新卡片位置
  public updateCardPosition(id: string, x: number, y: number): void {
    const index = this.cards.findIndex(card => card.id === id);
    if (index !== -1) {
      this.cards[index].x = x;
      this.cards[index].y = y;
    }
  }

  // 更新卡片内容
  public updateCardContent(id: string, content: string): void {
    const index = this.cards.findIndex(card => card.id === id);
    if (index !== -1) {
      // 确保换行符不被去除或修改
      this.cards[index].content = this.normalizeLineBreaks(content);
    }
  }

  // 删除卡片
  public deleteCards(indices: number[]): void {
    this.cards = this.cards.filter((_, index) => !indices.includes(index));
  }

  // 绘制所有卡片
  public drawAllCards(): void {
    if (!this.ctx) return;
    
    this.cards.forEach(card => {
      if (!card.isEditing) {
        this.drawCard(card);
      }
    });
  }

  // 备用渲染方法，对付顽固的换行问题
  private renderTextWithForcedLineBreaks(text: string, x: number, y: number, maxWidth: number, lineHeight: number, cardData?: CardData): void {
    if (!this.ctx) return;
    
    // 强制处理换行符，确保它们被正确显示
    const normalizedText = this.normalizeLineBreaks(text);
    const lines = normalizedText.split('\n');
    
    // 计算起始y坐标使文本垂直居中
    const totalHeight = lines.length * lineHeight;
    // 直接使用传入的卡片数据，而不是通过x坐标查找
    const cardHeight = cardData ? cardData.height : 120;
    const cardCenterY = cardData ? (cardData.y + cardHeight / 2) : y;
    const startY = cardCenterY - (totalHeight / 2) + (lineHeight / 2);
    
    // 绘制每一行
    let currentY = startY;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // 处理可能过长的行 (简单实现，只在两个点分割)
      if (this.ctx.measureText(line).width > maxWidth) {
        const halfPoint = Math.floor(line.length / 2);
        const firstHalf = line.substring(0, halfPoint);
        const secondHalf = line.substring(halfPoint);
        
        this.ctx.fillText(firstHalf, x, currentY);
        currentY += lineHeight;
        this.ctx.fillText(secondHalf, x, currentY);
      } else {
        this.ctx.fillText(line, x, currentY);
      }
      
      currentY += lineHeight;
    }
  }

  // 绘制单个卡片
  private drawCard(card: CardData): void {
    if (!this.ctx) return;
    
    // 调试信息：在渲染前检查卡片内容换行符
    if (process.env.NODE_ENV === 'development') {
      if (card.content.includes('\n')) {
        // 移除调试日志
      }
    }
    
    // 保存当前绘图状态
    this.ctx.save();

    // 创建圆角矩形路径
    const x = card.x;
    const y = card.y;
    const width = card.width;
    const height = card.height;
    const radius = card.borderRadius;
    
    // 绘制外边框，采用填充方式
    this.ctx.beginPath();
    this.createRoundedRectPath(x, y, width, height, radius);
    this.ctx.fillStyle = card.borderColor; // 先用边框颜色填充整个区域
    this.ctx.fill();
    
    // 内边框宽度
    const borderWidth = 2;
    
    // 绘制内部区域，覆盖外边框
    this.ctx.beginPath();
    this.createRoundedRectPath(
      x + borderWidth, 
      y + borderWidth, 
      width - borderWidth * 2, 
      height - borderWidth * 2, 
      Math.max(0, radius - borderWidth)
    );
    this.ctx.fillStyle = card.color; // 用背景色填充内部区域
    this.ctx.fill();
    
    // 绘制卡片内容
    this.ctx.fillStyle = '#000000'; // 文本颜色默认为黑色
    
    // 使用与编辑时相同的字体大小
    const baseFontSize = 16;
    this.ctx.font = `${baseFontSize}px Arial, sans-serif`;
    
    this.ctx.textBaseline = 'middle'; // 修改为middle以便垂直居中
    this.ctx.textAlign = 'center'; // 设置为center以便水平居中
    
    const padding = 8; // 减小内边距
    const lineHeight = baseFontSize * 1.4; // 使用与编辑时相同的行高比例
    const maxWidth = card.width - padding * 2; // 可用于文本的最大宽度
    
    // 计算卡片中心位置
    const centerX = card.x + card.width / 2;
    const centerY = card.y + card.height / 2;
    
    // 检查是否含有换行符，如果有使用备用渲染方法
    if (card.content.includes('\n')) {
      // 使用强制换行的简单渲染方法
      this.renderTextWithForcedLineBreaks(card.content, centerX, centerY, maxWidth, lineHeight, card);
    } else {
      // 使用优化后的文本渲染方法，处理正常文本
      this.drawTextWithWrapping(card.content, centerX, centerY, maxWidth, lineHeight, 1.0, card);
    }
    
    // 恢复绘图状态
    this.ctx.restore();
  }
  
  // 辅助方法：创建圆角矩形路径
  private createRoundedRectPath(x: number, y: number, width: number, height: number, radius: number): void {
    if (!this.ctx) return;
    
    if (radius === 0) {
      // 当radius为0时，直接绘制普通矩形
      this.ctx.rect(x, y, width, height);
    } else {
      // 绘制圆角矩形
      this.ctx.moveTo(x + radius, y);
      this.ctx.lineTo(x + width - radius, y);
      this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      this.ctx.lineTo(x + width, y + height - radius);
      this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      this.ctx.lineTo(x + radius, y + height);
      this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      this.ctx.lineTo(x, y + radius);
      this.ctx.quadraticCurveTo(x, y, x + radius, y);
    }
    this.ctx.closePath();
  }
  
  // 绘制带自动换行的文本
  private drawTextWithWrapping(text: string, x: number, y: number, maxWidth: number, lineHeight: number, relativeScale: number = 1.0, cardData?: CardData): void {
    if (!this.ctx) return;
    
    // 直接处理方式：按行渲染文本，不做额外分词处理
    // 首先按换行符分割文本以处理手动换行
    const normalizedText = this.normalizeLineBreaks(text);
    const lines = normalizedText.split('\n');
    
    // 计算总行数（考虑自动换行）
    let totalLines = 0;
    const wrappedLines: string[] = [];
    
    // 预处理：计算每行文本实际渲染后的行数并存储
    lines.forEach(line => {
      if (line.trim() === '') {
        // 空行算作一行
        totalLines++;
        wrappedLines.push('');
        return;
      }
      
      // 测量并拆分过长的行
      let remainingText = line;
      while (remainingText.length > 0) {
        let i = remainingText.length;
        let currentLine = remainingText;
        
        // 二分查找找出最大能放入宽度的文本长度
        while (this.ctx!.measureText(currentLine).width > maxWidth && i > 0) {
          i = Math.floor(i / 2);
          currentLine = remainingText.slice(0, i);
        }
        
        // 找到了一个能放入的长度，现在向右扩展找到最大可放入长度
        while (i < remainingText.length && 
               this.ctx!.measureText(remainingText.slice(0, i + 1)).width <= maxWidth) {
          i++;
        }
        
        // 如果一个字都放不下，至少放一个字
        if (i === 0 && remainingText.length > 0) {
          i = 1;
        }
        
        // 添加这一行并继续处理剩余文本
        wrappedLines.push(remainingText.slice(0, i));
        totalLines++;
        remainingText = remainingText.slice(i);
      }
    });
    
    // 计算文本开始的Y坐标（垂直居中）
    const totalHeight = totalLines * lineHeight;
    // 直接使用传入的卡片数据，而不是通过x坐标查找
    const cardHeight = cardData ? cardData.height : 120;
    const cardCenterY = cardData ? (cardData.y + cardHeight / 2) : y;
    const startY = cardCenterY - (totalHeight / 2) + (lineHeight / 2);
    
    // 绘制处理后的每一行
    let currentY = startY;
    wrappedLines.forEach(line => {
      // 即使是空行也要增加行高
      if (line.length === 0) {
        currentY += lineHeight;
        return;
      }
      
      // 绘制文本（居中对齐）
      this.ctx!.fillText(line, x, currentY);
      currentY += lineHeight;
    });
    
    // 用于调试
    if (process.env.NODE_ENV === 'development') {
      const wrappedText = wrappedLines.join('\n');
      if (wrappedText !== text) {
        // 移除调试日志
      }
    }
  }

  // 检测点是否在卡片内
  public hitTest(point: Point): number {
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i];
      
      // 如果点在卡片的边界内
      if (
        point.x >= card.x &&
        point.x <= card.x + card.width &&
        point.y >= card.y &&
        point.y <= card.y + card.height
      ) {
        return i; // 返回卡片索引
      }
    }
    
    return -1; // 没有命中任何卡片
  }

  // 开始编辑卡片
  public startEditing(cardIndex: number): void {
    if (cardIndex < 0 || cardIndex >= this.cards.length) return;
    
    // 检查是否已经有卡片正在编辑
    const isAnyCardEditing = this.cards.some(card => card.isEditing);
    if (isAnyCardEditing) return;
    
    // 设置卡片为编辑状态
    this.cards[cardIndex].isEditing = true;
    
    // 创建编辑器
    this.createCardEditor(this.cards[cardIndex]);
  }

  // 移除编辑器
  private removeCardEditor(cardId: string): void {
    const container = document.querySelector(`div[data-card-editor-container="true"][data-card-id="${cardId}"]`) as HTMLElement;
    if (container) {
      // 移除事件监听器
      if ((container as any)._containerClickHandler) {
        container.removeEventListener('mousedown', (container as any)._containerClickHandler);
      }
      if ((container as any)._documentClickHandler) {
        document.removeEventListener('mousedown', (container as any)._documentClickHandler);
      }
      if ((container as any)._viewportChangedHandler) {
        document.removeEventListener('viewport-changed', (container as any)._viewportChangedHandler);
      }
      if ((container as any)._viewportScrollUpdateHandler) {
        document.removeEventListener('viewport-scroll-update', (container as any)._viewportScrollUpdateHandler);
      }
      
      container.remove();
    }
    
    // 清理样式表
    if (!document.querySelector('[data-card-editor-container="true"]')) {
      const styleSheet = document.getElementById('card-editor-style');
      if (styleSheet) {
        document.head.removeChild(styleSheet);
      }
    }
  }

  /**
   * 更新编辑元素的位置
   */
  public updateEditingElementsPosition(): void {
    if (!this.viewportService) return;
    
    // 找到正在编辑的卡片
    const editingCard = this.cards.find(card => card.isEditing);
    if (!editingCard) return;
    
    // 获取当前缩放比例
    const currentScale = this.viewportService.getScale();
    // 获取创建时的缩放比例（如果存在的话）
    const creationScale = editingCard.creationScale || 1.0;
    
    // 转换canvas坐标到屏幕坐标（使用中心点）
    const centerX = editingCard.x + editingCard.width / 2;
    const centerY = editingCard.y + editingCard.height / 2;
    const screenPos = this.viewportService.canvasToClient(centerX, centerY);
    
    // 获取编辑器容器
    const container = document.querySelector(`div[data-card-editor-container="true"][data-card-id="${editingCard.id}"]`) as HTMLElement;
    if (!container) return;
    
    // 计算缩放后的卡片尺寸
    const scaledWidth = editingCard.width * currentScale;
    const scaledHeight = editingCard.height * currentScale;
    
    // 获取边框宽度和内边距
    const borderWidth = 2 * currentScale;
    const padding = 8 * currentScale;
    
    // 完全取消过渡动画，实时更新位置和大小
    container.style.transition = 'none';
    container.style.transformOrigin = 'center'; // 确保变换原点为中心
    
    // 使用transform来定位容器，而不是left/top属性，避免亚像素计算问题
    const translateX = Math.round(screenPos.x - scaledWidth / 2);
    const translateY = Math.round(screenPos.y - scaledHeight / 2);
    container.style.transform = `translate(${translateX}px, ${translateY}px)`;
    container.style.left = '0';
    container.style.top = '0';
    
    // 更新容器大小和样式，使用精确值
    container.style.width = `${scaledWidth}px`;
    container.style.height = `${scaledHeight}px`;
    container.style.border = `${borderWidth}px solid ${editingCard.borderColor}`;
    container.style.borderRadius = `${editingCard.borderRadius * currentScale}px`;
    container.style.padding = `${padding}px`;
    container.style.backgroundColor = editingCard.color;
    container.style.boxSizing = 'border-box';
    
    // 更新内部包装容器以确保完全填充并居中对齐
    const wrapperDiv = container.querySelector('div') as HTMLElement;
    if (wrapperDiv) {
      wrapperDiv.style.position = 'absolute';
      wrapperDiv.style.left = '0';
      wrapperDiv.style.top = '0';
      wrapperDiv.style.right = '0';
      wrapperDiv.style.bottom = '0';
      wrapperDiv.style.display = 'flex';
      wrapperDiv.style.flexDirection = 'column';
      wrapperDiv.style.justifyContent = 'center';
      wrapperDiv.style.alignItems = 'center';
    }
    
    // 更新文本编辑器
    const textarea = container.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      // 计算精确的文本域宽度
      const contentWidth = Math.max(10, scaledWidth - padding * 2 - borderWidth * 2);
      textarea.style.width = `${contentWidth}px`;
      
      // 使用固定的字体大小乘以当前缩放比例
      // 为了确保在较小缩放比例时仍保持可见性，设置最小字体大小为9px
      const fontSize = Math.max(9, 16 * currentScale);
      textarea.style.fontSize = `${fontSize}px`;
      
      textarea.style.lineHeight = '1.4';
      textarea.style.textAlign = 'center';
      textarea.style.alignSelf = 'center'; // 确保垂直居中
      textarea.style.margin = '0';
      textarea.style.padding = '0';
      
      // 使用setTimeout异步触发高度重新计算，确保在DOM更新后执行
      setTimeout(() => {
        const originalScrollHeight = textarea.scrollHeight;
        textarea.style.height = '0';
        const maxHeight = Math.max(20, container.clientHeight * 0.9 - borderWidth * 2 - padding * 2);
        textarea.style.height = `${Math.min(originalScrollHeight, maxHeight)}px`;
      }, 0);
    }
  }
} 